import { useState, useMemo } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  Plus, Pencil, Trash2, X, ArrowUpRight, ArrowDownRight, BarChart3,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import {
  useFinanceSummary, useFinanceEntries, useCreateEntry, useUpdateEntry, useDeleteEntry,
  useCustomCategories, type FinanceEntry,
} from '@/api/hooks/useFinancial'
import { useFinancePages } from '@/api/hooks/usePages'

const fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const REVENUE_CATEGORIES = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'kwai', label: 'Kwai' },
  { value: 'cifras', label: 'Cifras' },
  { value: 'adsense', label: 'AdSense' },
  { value: 'outro', label: 'Outro' },
]

const EXPENSE_CATEGORIES = [
  { value: 'grupo', label: 'Grupo' },
  { value: 'pagina', label: 'Compra de Pagina' },
  { value: 'perfil', label: 'Compra de Perfil' },
  { value: 'servidor', label: 'Servidor' },
  { value: 'ferramenta', label: 'Ferramenta' },
  { value: 'outro', label: 'Outro' },
]

const CATEGORY_LABELS: Record<string, string> = {
  facebook: 'Facebook', youtube: 'YouTube', tiktok: 'TikTok', kwai: 'Kwai',
  cifras: 'Cifras', adsense: 'AdSense', pagina: 'Pagina', outro: 'Outro',
  grupo: 'Grupo', perfil: 'Perfil', servidor: 'Servidor', ferramenta: 'Ferramenta',
}

const CAT_COLORS: Record<string, string> = {
  facebook: '#3b82f6', youtube: '#ef4444', tiktok: '#06b6d4', kwai: '#f59e0b',
  cifras: '#8b5cf6', adsense: '#10b981', outro: '#6b7280',
  grupo: '#ef4444', pagina: '#f97316', perfil: '#ec4899', servidor: '#3b82f6',
  ferramenta: '#8b5cf6',
}

/* ─── Category Bars ─── */
function CategoryBreakdown({ data, t }: {
  data: { category: string; total: number }[]
  t: (s: string) => string
}) {
  if (!data.length) return <p className="text-xs text-muted-foreground py-3">{t('Sem dados')}</p>
  const max = Math.max(...data.map(d => d.total))
  return (
    <div className="space-y-2.5">
      {data.map(d => {
        const pct = max > 0 ? (d.total / max) * 100 : 0
        const color = CAT_COLORS[d.category] || '#6b7280'
        return (
          <div key={d.category} className="group/cat">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-foreground">{t(CATEGORY_LABELS[d.category] || d.category)}</span>
              <span className="text-xs tabular-nums font-medium text-muted-foreground">{fmt.format(d.total)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ─── Entry Dialog ─── */
function EntryDialog({
  open, onOpenChange, editEntry,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editEntry: FinanceEntry | null
}) {
  const { t } = useLanguage()
  const { data: financePages } = useFinancePages()
  const { data: customCats } = useCustomCategories()
  const [type, setType] = useState<'revenue' | 'expense'>(editEntry?.type ?? 'revenue')
  const [category, setCategory] = useState(editEntry?.category ?? '')
  const [amount, setAmount] = useState(editEntry?.amount?.toString() ?? '')
  const [date, setDate] = useState(editEntry?.date ?? new Date().toISOString().split('T')[0])
  const [description, setDescription] = useState(editEntry?.description ?? '')
  const [selectedPageId, setSelectedPageId] = useState(editEntry?.financePageId ?? '')
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')

  const createMut = useCreateEntry()
  const updateMut = useUpdateEntry()
  const saving = createMut.isPending || updateMut.isPending

  const categories = useMemo(() => {
    const defaults = type === 'revenue' ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES
    const custom = (type === 'revenue' ? customCats?.revenue : customCats?.expense) ?? []
    const defaultValues = new Set(defaults.map(c => c.value))
    const customItems = custom.filter(c => !defaultValues.has(c)).map(c => ({ value: c, label: c }))
    return [...defaults, ...customItems]
  }, [type, customCats])

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setType(editEntry?.type ?? 'revenue')
      setCategory(editEntry?.category ?? '')
      setAmount(editEntry?.amount?.toString() ?? '')
      setDate(editEntry?.date ?? new Date().toISOString().split('T')[0])
      setDescription(editEntry?.description ?? '')
      setSelectedPageId(editEntry?.financePageId ?? '')
      setShowNewCategory(false)
      setNewCategoryName('')
    }
    onOpenChange(v)
  }

  const handleSave = () => {
    if (!category) { toast.error(t('Selecione a categoria')); return }
    if (!amount || parseFloat(amount) <= 0) { toast.error(t('Informe um valor valido')); return }
    if (!date) { toast.error(t('Informe a data')); return }

    const data = {
      type, category, amount: parseFloat(amount), date,
      description: description || undefined,
      financePageId: selectedPageId && selectedPageId !== 'none' ? selectedPageId : null,
    }

    if (editEntry) {
      updateMut.mutate({ id: editEntry.id, ...data }, {
        onSuccess: () => { toast.success(t('Lancamento atualizado!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    } else {
      createMut.mutate(data, {
        onSuccess: () => { toast.success(t('Lancamento criado!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editEntry ? t('Editar Lancamento') : t('Novo Lancamento')}</DialogTitle>
          <DialogDescription>{t('Registre uma receita ou despesa da operacao.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('Tipo')}</Label>
              <Select value={type} onValueChange={(v) => { setType(v as 'revenue' | 'expense'); setCategory(''); setSelectedPageId(''); setShowNewCategory(false); setNewCategoryName('') }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">{t('Receita')}</SelectItem>
                  <SelectItem value="expense">{t('Despesa')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('Categoria')}</Label>
              {showNewCategory ? (
                <div className="mt-1 flex items-center gap-1.5">
                  <Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder={t('Nome da categoria...')} className="h-9 text-sm" autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newCategoryName.trim()) { const val = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_'); setCategory(val); setShowNewCategory(false); setNewCategoryName('') }
                      if (e.key === 'Escape') { setShowNewCategory(false); setNewCategoryName('') }
                    }}
                  />
                  <Button type="button" size="sm" variant="outline" className="h-9 shrink-0"
                    onClick={() => { if (newCategoryName.trim()) { const val = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_'); setCategory(val); setShowNewCategory(false); setNewCategoryName('') } }}
                  >OK</Button>
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => { setShowNewCategory(false); setNewCategoryName('') }}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Select value={category} onValueChange={(v) => { if (v === '__new__') { setShowNewCategory(true); return }; setCategory(v) }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder={t('Selecione...')} /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.value} value={c.value}>{t(CATEGORY_LABELS[c.value] || c.label)}</SelectItem>)}
                    <SelectItem value="__new__" className="text-primary font-medium">+ {t('Nova Categoria')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('Valor (R$)')}</Label>
              <Input type="number" min={0} step={0.01} className="mt-1" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>{t('Data')}</Label>
              <Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          {financePages && financePages.length > 0 && (
            <div>
              <Label>{t('Pagina (opcional)')}</Label>
              <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t('Nenhuma')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('Nenhuma')}</SelectItem>
                  {financePages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t('Descricao (opcional)')}</Label>
            <Input className="mt-1" placeholder={t('Ex: Receita de anuncios janeiro, Pagamento grupo X...')} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? t('Salvando...') : t('Salvar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Main Page ─── */
export function Financial() {
  const { t, language } = useLanguage()
  const { data: summary, isLoading: loadingSummary } = useFinanceSummary()

  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [filterType, setFilterType] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editEntry, setEditEntry] = useState<FinanceEntry | null>(null)

  const { data: entries = [], isLoading: loadingEntries } = useFinanceEntries({
    month: filterMonth || undefined,
    type: filterType || undefined,
  })

  const deleteMut = useDeleteEntry()
  const openCreate = () => { setEditEntry(null); setDialogOpen(true) }
  const openEdit = (entry: FinanceEntry) => { setEditEntry(entry); setDialogOpen(true) }
  const handleDelete = (entry: FinanceEntry) => {
    if (!confirm(t('Deletar este lancamento?'))) return
    deleteMut.mutate(entry.id, {
      onSuccess: () => toast.success(t('Lancamento deletado')),
      onError: (e) => toast.error(e.message),
    })
  }

  const balance = summary?.balance ?? 0
  const totalBalance = (summary?.totalRevenue ?? 0) - (summary?.totalExpense ?? 0)
  const isPositive = balance >= 0
  const hasChart = summary?.monthlyChart.some(m => m.revenue > 0 || m.expense > 0)

  return (
    <div className="space-y-6 px-6 md:px-8 py-6 max-w-[1100px]">
      {/* Header */}
      <div className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Financeiro')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('Controle de receitas e despesas')}</p>
          </div>
          <Button className="gap-2" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t('Novo')}
          </Button>
        </div>
      </div>

      {loadingSummary ? (
        <div className="space-y-4">
          <Skeleton className="h-28 rounded-xl" />
          <div className="grid gap-4 sm:grid-cols-2"><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-20 rounded-xl" /></div>
        </div>
      ) : (
        <>
          {/* Balance Hero */}
          <div className={`rounded-xl border p-6 ${
            isPositive
              ? 'border-emerald-500/20 bg-emerald-500/[0.03] dark:bg-emerald-500/[0.05]'
              : 'border-red-500/20 bg-red-500/[0.03] dark:bg-red-500/[0.05]'
          }`}>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('Saldo do Mes')}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold tracking-tight ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fmt.format(balance)}
                  </span>
                  <div className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isPositive ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {isPositive ? t('Lucro') : t('Prejuizo')}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">{t('Acumulado')}</p>
                <span className="text-lg font-semibold">{fmt.format(totalBalance)}</span>
              </div>
            </div>

            {/* Inline revenue / expense */}
            <div className="flex gap-6 mt-4 pt-4 border-t border-border/40">
              <div>
                <p className="text-[11px] text-muted-foreground">{t('Receita')}</p>
                <p className="text-sm font-semibold text-emerald-500">{fmt.format(summary?.currentMonthRevenue ?? 0)}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">{t('Despesas')}</p>
                <p className="text-sm font-semibold text-red-400">{fmt.format(summary?.currentMonthExpense ?? 0)}</p>
              </div>
            </div>
          </div>

          {/* Chart + Categories */}
          {(hasChart || (summary?.revenueByCategory.length ?? 0) > 0 || (summary?.expenseByCategory.length ?? 0) > 0) && (
            <div className="grid gap-4 lg:grid-cols-5">
              {/* Bar Chart */}
              {hasChart && (
                <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold">{t('Ultimos 6 meses')}</h3>
                    <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />{t('Receita')}</span>
                      <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm bg-red-500" />{t('Despesa')}</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={summary!.monthlyChart} barGap={3}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis
                        dataKey="month" tickLine={false}
                        axisLine={{ stroke: 'var(--border)' }}
                        tick={({ x, y, payload }) => (
                          <text x={x} y={y} dy={14} textAnchor="middle" style={{ fill: 'var(--muted-foreground)', fontSize: 11 }}>{payload.value}</text>
                        )}
                      />
                      <YAxis
                        tickLine={false} axisLine={false} width={50}
                        tick={({ x, y, payload }) => {
                          const v = Number(payload.value)
                          const label = v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                          return <text x={x} y={y} dx={-4} textAnchor="end" style={{ fill: 'var(--muted-foreground)', fontSize: 11 }}>{label}</text>
                        }}
                      />
                      <Tooltip
                        cursor={{ fill: 'var(--muted-foreground)', opacity: 0.05 }}
                        content={({ active, payload, label }: any) => {
                          if (!active || !payload) return null
                          return (
                            <div className="rounded-lg border border-border/60 bg-card/95 backdrop-blur-sm p-2.5 shadow-lg text-xs">
                              <p className="font-semibold text-foreground mb-1.5">{label}</p>
                              {payload.map((p: any) => (
                                <div key={p.name} className="flex items-center justify-between gap-4">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                                    {p.name === 'revenue' ? t('Receita') : t('Despesa')}
                                  </span>
                                  <span className="font-semibold text-foreground">{fmt.format(p.value)}</span>
                                </div>
                              ))}
                            </div>
                          )
                        }}
                      />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} name="revenue" maxBarSize={28} />
                      <Bar dataKey="expense" fill="#ef4444" radius={[4, 4, 0, 0]} name="expense" maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Category breakdown */}
              <div className={`rounded-xl border border-border bg-card p-5 ${hasChart ? 'lg:col-span-2' : 'lg:col-span-5'}`}>
                <h3 className="text-sm font-semibold mb-4">{t('Categorias do mes')}</h3>
                <div className="space-y-5">
                  {(summary?.revenueByCategory.length ?? 0) > 0 && (
                    <div>
                      <p className="text-[11px] font-medium text-emerald-500 uppercase tracking-wide mb-2">{t('Receitas')}</p>
                      <CategoryBreakdown data={summary!.revenueByCategory} t={t} />
                    </div>
                  )}
                  {(summary?.expenseByCategory.length ?? 0) > 0 && (
                    <div>
                      <p className="text-[11px] font-medium text-red-400 uppercase tracking-wide mb-2">{t('Despesas')}</p>
                      <CategoryBreakdown data={summary!.expenseByCategory} t={t} />
                    </div>
                  )}
                  {(summary?.revenueByCategory.length ?? 0) === 0 && (summary?.expenseByCategory.length ?? 0) === 0 && (
                    <p className="text-xs text-muted-foreground py-4 text-center">{t('Sem dados')}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Entries */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-border/40">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{t('Lancamentos')}</h3>
            {entries.length > 0 && (
              <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded-md px-1.5 py-0.5">{entries.length}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue placeholder={t('Todos')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Todos')}</SelectItem>
                <SelectItem value="revenue">{t('Receitas')}</SelectItem>
                <SelectItem value="expense">{t('Despesas')}</SelectItem>
              </SelectContent>
            </Select>
            <Input type="month" className="w-[140px] h-8 text-xs" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} />
            {(filterType || filterMonth) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground" onClick={() => { setFilterType(''); setFilterMonth('') }}>
                <X className="h-3 w-3" /> {t('Limpar')}
              </Button>
            )}
          </div>
        </div>

        {loadingEntries ? (
          <div className="space-y-1 p-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-11 rounded-lg" />)}</div>
        ) : entries.length === 0 ? (
          <div className="py-14 text-center">
            <BarChart3 className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('Nenhum lancamento encontrado.')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30 hover:bg-transparent">
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('Data')}</TableHead>
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('Tipo')}</TableHead>
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('Categoria')}</TableHead>
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('Pagina')}</TableHead>
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('Descricao')}</TableHead>
                  <TableHead className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground text-right">{t('Valor')}</TableHead>
                  <TableHead className="w-[72px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} className="border-border/20 group/row">
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(entry.date + 'T12:00:00').toLocaleDateString(language === 'en' ? 'en-US' : 'pt-BR')}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-xs">
                        <span className={`h-1.5 w-1.5 rounded-full ${entry.type === 'revenue' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {entry.type === 'revenue' ? t('Receita') : t('Despesa')}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{t(CATEGORY_LABELS[entry.category] || entry.category)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{entry.financePageName || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{entry.description || '-'}</TableCell>
                    <TableCell className={`text-xs text-right font-semibold tabular-nums ${entry.type === 'revenue' ? 'text-emerald-500' : 'text-red-400'}`}>
                      {entry.type === 'revenue' ? '+' : '-'}{fmt.format(entry.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(entry)}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(entry)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {dialogOpen && <EntryDialog open={dialogOpen} onOpenChange={setDialogOpen} editEntry={editEntry} />}
    </div>
  )
}
